import {Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ISchulung} from '../../../shared/model/schulung.model';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {DatePipe} from '@angular/common';
import {environment} from '../../../../environments/environment';
import {BeraterService} from '../../../services/berater.service';
import {Berater} from '../../../shared/model/berater.model';
import {MatButton, MatIconButton, MatMiniFabButton} from '@angular/material/button';
import {
  MatDatepicker,
  MatDatepickerActions,
  MatDatepickerApply,
  MatDatepickerCancel,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {MatError} from '@angular/material/form-field';
import {MatFormField, MatInput, MatLabel, MatSuffix} from '@angular/material/input';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatOption} from '@angular/material/core';
import {AddCertificateComponent} from '../../../shared/components/add-certificate/add-certificate.component';
import {TranslatePipe} from '@ngx-translate/core';
import {CertificateUploadService} from '../../../services/zertifikat-upload.service';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatIcon} from '@angular/material/icon';
import {MessageService} from '../../../shared/components/message.service';
import {DateService} from '../../../services/date.service';
import {ModalComponent} from '../../../shared/components/modal/modal.component';
import {ModalService} from '../../../shared/components/modal/modal.service';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {Requests, RequestsFactory, RequestType} from '../../../shared/model/request.model';
import {RequestsService} from '../../../services/requests.service';
import {UserRoleService} from '../../../services/userRole.service';
import {INPUT_LIMITS} from '../../../shared/constants/input-limits';
import {FieldWarningComponent} from '../../../shared/components/field-warning/field-warning.component';
import {ValidationFinding, findEntryFinding} from '../../../shared/model/validation-finding';

@Component({
  selector: 'app-trainings',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatButton,
    MatLabel,
    MatFormField,
    MatError,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatInput,
    MatDatepickerInput,
    MatCheckbox,
    MatDatepickerActions,
    MatDatepickerCancel,
    MatDatepickerApply,
    MatOption,
    AddCertificateComponent,
    TranslatePipe,
    MatCard,
    MatIcon,
    MatMiniFabButton,
    MatCardContent,
    ModalComponent,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatIconButton,
    FormsModule,
    FieldWarningComponent
  ],
  templateUrl: './trainings.component.html',
  styleUrl: './trainings.component.scss',
})
export class TrainingsComponent implements OnInit, OnChanges {
  @Input() schulungen: ISchulung[] = [];
  @Input() berater: Berater;
  @Input() exportMode: boolean;
  @Input() twoPagerMode: boolean = false;
  @Input() forcedSelectedIds: string[] | null = null;
  @Input() findings: ValidationFinding[] = [];

  @Output() exportSelectionChange = new EventEmitter<string[]>();
  // NP20-828: Parent uebernimmt den gelieferten Berater direkt, statt neu zu laden.
  @Output() saved = new EventEmitter<Berater>();

  selectedExportIds: string[] = [];
  filteredTrainingCourses: Array<string[]> = [];
  form!: FormGroup;
  isCancelDisabled: boolean = false;
  editMode = false;
  /** NP20-822: Laeuft gerade ein Speichern, loest ein weiterer Klick keinen zweiten Request aus. */
  isSaving = false;
  uploadedFiles = new Map<number, string>();
  schulungenCopy: ISchulung[];
  protected readonly environment = environment;
  protected get LIMITS() { return INPUT_LIMITS; }
  trainingsNames: string[];
  isPopupOpen: boolean;
  formChanged: boolean = false;
  originalTrainingCourses: ISchulung[];
  showModal = false;
  showModalAttachment = false;
  showModalAddRequest = false;
  currentIdx: number | null = null;
  newReq: Requests;
  addRequestFormInvalid: boolean = true;

  constructor(
    private fb: FormBuilder,
    private certificateUploadService: CertificateUploadService,
    private http: HttpClient,
    @Inject(LOCALE_ID) public locale: string,
    private beraterService: BeraterService,
    private messageService: MessageService,
    private dateService: DateService,
    private modalService: ModalService,
    private requestsService: RequestsService,
    private userService: UserRoleService
  ) {
  }

  get schulungenFormArray(): FormArray {
    return this.form.get('schulungen') as FormArray;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['schulungen'] && changes['schulungen'].currentValue) {
      this.schulungenCopy = JSON.parse(
        JSON.stringify(changes['schulungen'].currentValue)
      );
    }
    if (changes['exportMode'] || changes['twoPagerMode']) {
      if (this.exportMode && !this.twoPagerMode) {
        this.autoSelectAll();
      } else {
        this.selectedExportIds = [];
      }
    } else if (changes['schulungen'] && this.exportMode && !this.twoPagerMode) {
      this.autoSelectAll();
    }

    if (changes['forcedSelectedIds'] && this.forcedSelectedIds !== null) {
      this.selectedExportIds = [...this.forcedSelectedIds];
      this.exportSelectionChange.emit([...this.selectedExportIds]);
    }
  }

  private autoSelectAll(): void {
    this.selectedExportIds = (this.schulungen ?? []).map(s => s.uuid).filter((uuid): uuid is string => !!uuid);
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  toggleExportSelection(idx: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedExportIds.includes(idx)) this.selectedExportIds.push(idx);
    } else {
      this.selectedExportIds = this.selectedExportIds.filter(i => i !== idx);
    }
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  ngOnInit() {
    this.requestsService
      .getRequests(0, 600, RequestType.TRAINING, true)
      .subscribe((value) => {
        this.trainingsNames = value.content.map((item: any) => item.name);
      });
    this.form = this.fb.group({
      schulungen: this.fb.array([])
    });
    if (this.schulungen?.length > 0) {
      this.schulungen.forEach((z) => this.addSchulungForm(z));
    }
  }

  addSchulungForm(data?: ISchulung) {
    const group = this.fb.group({
      uuid: [data?.uuid || null],
      beginn: [data?.beginn || '', Validators.required],
      schulungsanbieter: [data?.schulungsanbieter || '', [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)]],
      lehrgang: [data?.lehrgang || '', [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)]],
      forExport: [data?.forExport || false],
      uploadId: [data?.uploadId || null]
    });
    this.schulungenFormArray.push(group);
  }

  async save() {
    if (this.form.invalid || this.isSaving) return;

    this.schulungen = this.schulungenFormArray.getRawValue();

    this.schulungen = this.schulungen.map((item) => ({
      ...item,
      beginn: this.dateService.toLocalDateString(item.beginn)
    }));
    // NP20-915: editMode erst nach der Antwort schliessen, sonst sieht ein Fehlschlag wie Erfolg aus.
    this.isSaving = true;
    this.beraterService
      .updateSchulungen(this.schulungen, this.berater.id)
      .subscribe({
        next: (value) => {
          this.isSaving = false;
          this.editMode = false;
          this.berater = value;
          this.isCancelDisabled = false;
          this.schulungen = this.berater.schulungen;
          this.schulungenCopy = JSON.parse(JSON.stringify(this.schulungen));
          this.messageService.success('trainings.successUpdateMessage');
          this.saved.emit(value);
        },
        error: () => {
          this.isSaving = false;
          // Bearbeitungsmodus bleibt offen, die Eingaben stehen weiter im Formular.
          this.messageService.error('trainings.errorUpdateMessage');
        },
      });
  }

  // NP20-916: Loeschen nur vormerken — Speichern schickt es mit, Abbrechen nimmt es zurueck.
  // Vorher lief sofort ein PUT und der Abbrechen-Knopf wurde gesperrt.
  delete(index: number) {
    this.schulungenFormArray.removeAt(index);
    this.formChanged = this.hasChanges();
    this.messageService.info('trainings.infoDeleteMessage');
  }

  deleteAttachment(index: number) {
    const uploadId = this.schulungenFormArray.at(index).value.uploadId;
    if (!uploadId) return;
    this.isCancelDisabled = true;
    this.certificateUploadService.delete(uploadId).subscribe({
      next: () => {
        this.schulungenFormArray.at(index).patchValue({uploadId: null});
        this.uploadedFiles.delete(index);
        this.messageService.success('trainings.successUploadDeleteMessage');
      },
      error: () => {
        this.messageService.error('trainings.errorTrainingUpload');
      }
    });
  }

  addAttachment(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];

      this.certificateUploadService.post(base64).subscribe({
        next: (uploadId) => {
          const group = this.schulungenFormArray.at(index) as FormGroup;
          group.patchValue({uploadId});
          this.isCancelDisabled = true;
          this.messageService.success('trainings.successUploadMessage');
        },
        error: (err) => console.error('Upload error:', err)
      });
    };
    reader.readAsDataURL(file);
  }

  async downloadSchulung(uploadId: string) {
    const url = `${this.environment.apiConfig.uri}api/zertifikate/${uploadId}`;

    try {
      const localToken = this.userService.getJWT();
      const headers = new HttpHeaders({
        Authorization: `Bearer ${localToken}`
      });
      const blob = await firstValueFrom(
        this.http.get(url, {headers, responseType: 'blob'})
      );

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `Schulung-${uploadId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Fehler beim Herunterladen:', err);
    }
  }

  onTrainingSaved() {
    this.requestsService
      .getRequests(0, 600, RequestType.TRAINING, true)
      .subscribe((value) => {
        this.trainingsNames = value.content.map((item: any) => item.name);
        this.filteredTrainingCourses = this.schulungen.map(() => [
          ...this.trainingsNames
        ]);
      });
  }

  onToggleEdit(): void {
    if (this.editMode) {
      this.save();
    } else {
      if (this.schulungen.length === 0) {
        this.addSchulungForm();
      } else {
        this.schulungenFormArray.clear();
        this.schulungen.forEach((z) => this.addSchulungForm(z));
      }
      this.originalTrainingCourses = JSON.parse(
        JSON.stringify(this.schulungen)
      );
      this.schulungenCopy = JSON.parse(
        JSON.stringify(this.form.value.schulungen)
      );

      this.editMode = true;
      this.formChanged = false;
    }
  }

  onCancelEdit(): void {
    this.schulungen = JSON.parse(JSON.stringify(this.originalTrainingCourses));

    this.schulungenFormArray.clear();
    this.schulungen.forEach((z) => this.addSchulungForm(z));

    this.editMode = false;
    this.formChanged = false;
  }

  hasChanges(): boolean {
    return (
      JSON.stringify(this.form.value.schulungen) !==
      JSON.stringify(this.schulungenCopy)
    );
  }

  openDeleteModal(idx: number) {
    this.currentIdx = idx;
    this.modalService.open('deleteTrainings');
  }

  filterTrainings(value: string, index: number) {
    this.filteredTrainingCourses[index] = this.trainingsNames.filter(
      (training) => training.toLowerCase().includes(value.toLowerCase())
    );
  }

  optionSelected(event: any, index: number) {
    this.schulungenFormArray
      .at(index)
      .get('lehrgang')!
      .setValue(event.option.value);
  }

  validateCourseSelection(index: number) {
    const control = this.schulungenFormArray.at(index).get('lehrgang');
    const value = control!.value;
    if (!this.trainingsNames.includes(value)) {
      control!.setValue('');
    }
  }

  openDeleteAttachmentModal(idx: number) {
    this.currentIdx = idx;
    this.showModalAttachment = true;
  }

  addTrainingRequest() {
    this.requestsService
      .newRequest(RequestType.TRAINING, this.newReq)
      .subscribe({
        next: () =>
          this.messageService.success('request.modals.successEditing'),
        error: () => this.messageService.error('request.modals.errorEditing')
      });
  }

  onAddRequestFormChange() {
    this.addRequestFormInvalid = !this.newReq?.name?.trim();
  }

  onAddRequestClick() {
    this.showModalAddRequest = true;
    this.newReq = RequestsFactory.empty();
  }


  entryFinding(entryIndex: number, field: string): ValidationFinding | null {
    if (this.editMode || this.exportMode) return null;
    return findEntryFinding(this.findings, entryIndex, field);
  }
}
